import React, { useMemo, useState, useEffect } from "react";
import Chart from "react-apexcharts";
import { format, startOfYear } from "date-fns";
import { fetchResidualVolume } from "../../../../services/residuals";
import useResidualStore from "../../../../stores/residuals";
import { useMediaQuery } from "../../../../hooks/useMediaQuery";
import styles from "./style.module.scss";
import SkeletonLoader from "../../../../components/skeleton-loader";

const CHART_HEIGHT_DESKTOP = 400;
const CHART_HEIGHT_MOBILE = 440;

function ResidualVolumeChart() {
  const filters = useResidualStore((state) => state.filters);
  const [volumeData, setVolumeData] = useState({});
  const [loading, setLoading] = useState(false);
  const isMobile = useMediaQuery(768);
  const chartHeight = isMobile ? CHART_HEIGHT_MOBILE : CHART_HEIGHT_DESKTOP;

  useEffect(() => {
    const fetchCurrentYearData = async () => {
      setLoading(true);
      try {
        
        const data = await fetchResidualVolume(filters);
        setVolumeData(data || {});
      } catch (error) {
        console.error('Error fetching current year volume:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchCurrentYearData();
  }, [filters]);

  // Get the current year's data
  const { filteredDates, filteredVolume } = useMemo(() => {
    if (!volumeData?.dates || !volumeData?.volume) {
      return { filteredDates: [], filteredVolume: [] };
    }

    return {
      filteredDates: volumeData.dates,
      filteredVolume: volumeData.volume.map(v => parseFloat(v) || 0)
    };
  }, [volumeData]);

  const chartLabels = useMemo(() => {
    return filteredDates;
  }, [filteredDates]);



  const chartOptions = useMemo(
    () => ({
      chart: {
        type: "line",
        height: chartHeight,
        toolbar: {
          show: false,
        },
        zoom: {
          enabled: false,
        },
        fontFamily: "Work Sans, sans-serif",
        background: "#FFFFFF",
        animations: {
          enabled: true,
          easing: "easeinout",
          speed: 800,
          animateGradually: {
            enabled: true,
            delay: 150,
          },
          dynamicAnimation: {
            enabled: true,
            speed: 350,
          },
        },
      },
      colors: ["#4C70EF", "#37B450", "#4C70EF"],
      dataLabels: {
        enabled: false,
      },
      stroke: {
        curve: "smooth",
        width: [0, 2, 2],
        lineCap: "round",
      },
      fill: {
        type: ["gradient", "none", "none"],
        gradient: {
          shade: "light",
          type: "vertical",
          opacityFrom: 0.6,
          opacityTo: 0.25,
          stops: [0, 100],
        },
      },
      xaxis: {
        categories: chartLabels,
        axisBorder: {
          show: false,
        },
        axisTicks: {
          show: false,
        },
        labels: {
            style: {
              colors: "#A4A3A8",
              fontSize: "12px",
              fontFamily: "Work Sans",
              fontWeight: 400,
            },
            rotate: 0,
            hideOverlappingLabels: true,
            showDuplicates: false,
            trim: false,
            formatter: function(value) {
              if (!value || typeof value !== 'string') return '';
              
              // Only show odd-numbered months (Jan, Mar, May, Jul, Sep, Nov)
              const monthsToShow = ['Jan', 'Mar', 'May', 'Jul', 'Sep', 'Nov'];
              const [month, year] = value.split(' ');
              
              if (!monthsToShow.includes(month)) return '';
              return `${month} ${year}`;
            }
          },
      },
      yaxis: {
        labels: {
          style: {
            colors: "#A4A3A8",
            fontSize: "12px",
            fontFamily: "Work Sans",
            fontWeight: 400,
          },
          formatter: (value) => {
            return `$ ${(value / 1000).toFixed(0)}K`;
          },
        },
        tickAmount: 6,
        min: 420000,
        max: 520000,
      },
      grid: {
        show: true,
        borderColor: "#DEDEE0",
        strokeDashArray: 0,
        position: "back",
        xaxis: {
          lines: {
            show: true,
          },
        },
        yaxis: {
          lines: {
            show: true,
            opacity: 0.5,
          },
        },
        padding: {
          top: 0,
          right: 20,
          bottom: 8,
          left: 16,
        },
      },
      yaxis: {
        min: undefined,
        max: undefined,
        tickAmount: 5,
        labels: {
          formatter: (value) => {
            return `$ ${(value / 1000000).toFixed(1)}M`;
          },
          style: {
            colors: "#A4A3A8",
            fontSize: "12px",
            fontFamily: "Work Sans",
            fontWeight: 400,
          },
        },
      },
      tooltip: {
        custom: function ({ series, dataPointIndex }) {
          const date = chartLabels[dataPointIndex];
          const totalVolume = parseFloat(series[0][dataPointIndex]) || 0;
          const processorResiduals = parseFloat(series[1][dataPointIndex]) || 0;

          // Calculate percentage change from previous day
          let percentageChange = 0;
          if (dataPointIndex > 0) {
            const previousVolume = parseFloat(series[0][dataPointIndex - 1]) || 0;
            if (previousVolume !== 0) {
              percentageChange = ((totalVolume - previousVolume) / previousVolume) * 100;
            }
          }

          // Format numbers
          const formattedVolume = new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }).format(totalVolume);

          const formattedResiduals = new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }).format(processorResiduals);

          const formattedPercentage = new Intl.NumberFormat('en-US', {
            style: 'percent',
            minimumFractionDigits: 1,
            maximumFractionDigits: 1,
          }).format(Math.abs(percentageChange / 100));

          return `
            <div class="${styles.tooltip}">
              <div class="${styles.tooltipHeader}">${date}</div>
              <div class="${styles.tooltipContent}">
                <div class="${styles.tooltipRow}">
                  <div class="${styles.tooltipLabel}">
                    <span class="${styles.tooltipDot}" style="background: #4C70EF"></span>
                    Total volume:
                  </div>
                  <div class="${styles.tooltipValue}">
                    ${formattedVolume}
                    ${percentageChange !== 0 ? `
                      <span style="color: ${percentageChange > 0 ? '#37B450' : '#FF4D4F'}; margin-left: 8px;">
                        ${percentageChange > 0 ? '↑' : '↓'} ${formattedPercentage}
                      </span>
                    ` : ''}
                  </div>
                </div>
                <div class="${styles.tooltipRow}">
                  <div class="${styles.tooltipLabel}">
                    <span class="${styles.tooltipDot}" style="background: #37B450"></span>
                    Processor residuals:
                  </div>
                  <div class="${styles.tooltipValue}">
                    ${formattedResiduals}
                  </div>
                </div>
              </div>
            </div>
          `;
        },
        shared: true,
        intersect: false,
        fixed: {
          enabled: true,
          position: "topRight",
          offsetX: 0,
          offsetY: 0,
        },
      },
      legend: {
        show: true,
        position: "bottom",
        horizontalAlign: isMobile ? "center" : "left",
        offsetX: isMobile ? 0 : 16,
        offsetY: isMobile ? 4 : 6,
        markers: {
          width: 8,
          height: 8,
          radius: 99,
          offsetX: -4,
        },
        itemMargin: {
          horizontal: isMobile ? 12 : 16,
          vertical: isMobile ? 4 : 6,
        },
        labels: {
          colors: "#626168",
          useSeriesColors: false,
          fontSize: "12px",
          fontFamily: "Work Sans",
          fontWeight: 400,
        },
        onItemClick: {
          toggleDataSeries: false
        },
        onItemHover: {
          highlightDataSeries: false
        },
      },
    }),
    [chartLabels, chartHeight, isMobile],
  );

  const series = useMemo(
    () => {
      return [
        {
          name: "Total volume",
          type: "area",
          data: filteredVolume,
        },
        {
          name: "Processor residuals",
          type: "line",
          data: filteredVolume.map(v => parseFloat(v) || 0),
        },
      ];
    },
    [filteredVolume],
  );

  if (loading) {
    return (
      <div className={`${styles.volumeChart} ${styles.volumeChartTooltip}`}>
        <SkeletonLoader variant="rectangular" width="100%" height={chartHeight} />
      </div>
    );
  }

  return (
    <div className={`${styles.volumeChart} ${styles.volumeChartTooltip}`}>
      <Chart options={chartOptions} series={series} type="line" height={chartHeight} />
    </div>
  );
}

export default ResidualVolumeChart;